import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, from, map, of, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  RoutingSlip,
  RoutingSlipDetails,
  RoutingSlipFilters,
  RoutingSlipResponse,
  Comment,
  UserAssignment,
  RoutingSlipDocumentResponse,
  UserAssignmentResponse,
  SigningDocumentKind,
} from '../models/routing-slip.model';

/** Result of GET .../download for document viewer (binary file or JSON with a URL). */
export type RoutingSlipDocumentViewerPayload =
  | { kind: 'blob'; blob: Blob; filename: string; type: string }
  | { kind: 'url'; url: string; filename: string; type: string };

@Injectable({
  providedIn: 'root',
})
export class RoutingSlipService {
  private apiUrl = `${environment.apiUrl}/RoutingSlip`;

  constructor(private http: HttpClient) {}

  /**
   * Get all routing slips with pagination, sorting, and filtering
   * @param pageNumber - Page number (default: 1)
   * @param pageSize - Number of items per page (default: 10)
   * @param sortBy - Sort criteria (default: 'newest')
   * @param filter - Filter criteria (default: 'all')
   * @param search - Search term (optional)
   * @param showDeleted - Show deleted/archived items (optional)
   * @returns Observable<RoutingSlipResponse>
   */
  getAllRoutingSlips(
    pageNumber: number = 1,
    pageSize: number = 10,
    sortBy: string = 'newest',
    filter: string = 'all',
    search?: string,
    showDeleted?: boolean
  ): Observable<RoutingSlipResponse> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString())
      .set('sortBy', sortBy)
      .set('filter', filter);

    if (search) {
      params = params.set('search', search);
    }

    if (showDeleted) {
      params = params.set('showDeleted', 'true');
    }

    return this.http.get<RoutingSlipResponse>(`${this.apiUrl}/getall`, {
      params,
    });
  }

  /**
   * Get permissions for routing slip assignment
   * @returns Observable<{id: number, name: string}[]>
   */
  getPermissions(): Observable<{ id: number; name: string }[]> {
    return this.http.get<any[]>(`${this.apiUrl}/permissions`);
  }

  /**
   * Get routing slips with permissions for the current user
   * This API returns routing slips with permission information
   * @returns Observable<Array with permission data>
   */
  getRoutingSlipsWithPermissions(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/slipspermissions`);
  }

  /**
   * Parse date string in DD-MM-YYYY format to Date object
   */
  private parseDate(dateString: string): Date {
    const [day, month, year] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
  }

  getRoutingSlipById(id: string): Observable<RoutingSlipDetails> {
    return this.http.get<RoutingSlipDetails>(`${this.apiUrl}/${id}`);
  }

  /**
   * Create a new routing slip document using multipart/form-data
   * @param documentName - Name of the document
   * @param dueDate - Due date for the routing slip
   * @param file - File to upload
   * @returns Observable<RoutingSlipDocumentResponse> - Returns the created routing slip with ID
   */
  createRoutingSlipDocument(
    documentName: string,
    dueDate: string,
    file: File
  ): Observable<RoutingSlipDocumentResponse> {
    const formData = new FormData();
    formData.append('DocumentName', documentName);
    formData.append('DueDate', dueDate);
    formData.append('File', file);

    return this.http.post<RoutingSlipDocumentResponse>(`${this.apiUrl}/createroutingslip`, formData);
  }

  /**
   * Assign users to a routing slip
   * @param routingSlipId - ID of the routing slip
   * @param assignments - Array of user assignments with permissions and step order
   * @returns Observable<UserAssignmentResponse>
   */
  assignUsersToRoutingSlip(
    routingSlipId: string,
    assignments: UserAssignment[]
  ): Observable<UserAssignmentResponse> {
    return this.http.post<UserAssignmentResponse>(
      `${this.apiUrl}/${routingSlipId}/assignusers`,
      assignments,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  }


  updateRoutingSlip(
    id: string,
    routingSlip: Partial<RoutingSlip>
  ): Observable<RoutingSlip> {
    return this.http.put<RoutingSlip>(`${this.apiUrl}/${id}`, routingSlip);
  }

  deleteRoutingSlip(id: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/${id}/delete`);
  }

  /**
   * Add a comment to a routing slip
   * @param id - Routing slip ID
   * @param comment - Comment string
   * @returns Observable<Comment>
   */
  addComment(id: string, comment: string): Observable<Comment> {
    return this.http.post<Comment>(
      `${this.apiUrl}/${id}/comments`,
      `"${comment}"`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  }

  /**
   * Body for `POST /api/RoutingSlip/{id}/approvereject`.
   */
  approveRejectRoutingSlip(
    id: string,
    isApproved: boolean,
    comment: string
  ): Observable<any> {
    const body: { isApproved: boolean; comment: string } = {
      isApproved,
      comment: comment ?? '',
    };

    return this.http.post<any>(`${this.apiUrl}/${id}/approvereject`, body, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  /**
   * Get audit log for a routing slip
   * @param documentId - Routing slip ID (used as document ID)
   * @returns Observable<any[]>
   */
  getAuditLog(documentId: string): Observable<any[]> {
    const baseUrl = environment.apiUrl.replace('/api', '');
    const apiUrl = `${baseUrl}/${documentId}/getauditlog`;
    return this.http.get<any[]>(apiUrl);
  }

  /**
   * Download a routing slip document (binary response).
   * @param id - Routing slip ID
   * @returns Observable<{blob: Blob, filename: string}>
   */
  downloadRoutingSlipDocument(id: string): Observable<{blob: Blob, filename: string, type: string}> {
    return this.http.get(`${this.apiUrl}/${id}/download`, {
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      map((response: HttpResponse<Blob>) =>
        this.mapBlobDownloadResponse(id, response)
      )
    );
  }

  /**
   * GET .../download for viewer: supports binary file or JSON body with a document URL.
   */
  getRoutingSlipDocumentForViewer(
    id: string
  ): Observable<RoutingSlipDocumentViewerPayload> {
    return this.http
      .get(`${this.apiUrl}/${id}/download`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        switchMap((response: HttpResponse<Blob>) => {
          const body = response.body;
          if (!body) {
            return throwError(() => new Error('Empty download response'));
          }
          const contentType = (
            response.headers.get('content-type') || ''
          ).toLowerCase();
          if (contentType.includes('application/json')) {
            return from(body.text()).pipe(
              map((text) => {
                let parsed: Record<string, unknown>;
                try {
                  parsed = JSON.parse(text) as Record<string, unknown>;
                } catch {
                  throw new Error('Invalid JSON in download response');
                }
                const url = this.extractDocumentUrlFromJson(parsed);
                if (!url) {
                  throw new Error('No document URL in download response');
                }
                const filename =
                  this.pickStringFromRecord(parsed, [
                    'fileName',
                    'filename',
                    'FileName',
                    'documentName',
                    'DocumentName',
                  ]) || `${id}.pdf`;
                const type = this.mimeTypeFromFilename(filename);
                return {
                  kind: 'url' as const,
                  url: url,
                  filename,
                  type,
                };
              })
            );
          }
          const mapped = this.mapBlobDownloadResponse(id, response);
          return of({
            kind: 'blob' as const,
            blob: mapped.blob,
            filename: mapped.filename,
            type: mapped.type,
          });
        })
      );
  }

  private mapBlobDownloadResponse(
    id: string,
    response: HttpResponse<Blob>
  ): { blob: Blob; filename: string; type: string } {
    const contentDisposition = response.headers.get('content-disposition');
    let filename = `${id}.pdf`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      );
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    const contentType =
      response.headers.get('content-type') || 'application/pdf';

    if (
      contentType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      if (!filename.toLowerCase().endsWith('.docx')) {
        filename = filename.replace(/\.[^/.]+$/, '') + '.docx';
      }
    } else if (contentType === 'application/msword') {
      if (!filename.toLowerCase().endsWith('.doc')) {
        filename = filename.replace(/\.[^/.]+$/, '') + '.doc';
      }
    } else if (contentType === 'application/pdf') {
      if (!filename.toLowerCase().endsWith('.pdf')) {
        filename = filename.replace(/\.[^/.]+$/, '') + '.pdf';
      }
    }

    return {
      blob: response.body!,
      filename,
      type: contentType,
    };
  }

  private extractDocumentUrlFromJson(
    body: Record<string, unknown>
  ): string | null {
    const raw = this.pickStringFromRecord(body, [
      'url',
      'documentUrl',
      'fileUrl',
      'downloadUrl',
      'presignedUrl',
      'signedUrl',
      'Url',
      'DocumentUrl',
      'FileUrl',
      'DownloadUrl',
    ]);
    return raw ? raw.trim() : null;
  }

  private pickStringFromRecord(
    obj: Record<string, unknown>,
    keys: string[]
  ): string {
    for (const k of keys) {
      const v = obj[k];
      if (typeof v === 'string' && v.trim()) {
        return v.trim();
      }
    }
    return '';
  }

  private mimeTypeFromFilename(filename: string): string {
    const extension = filename.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'application/pdf';
      case 'doc':
        return 'application/msword';
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'txt':
        return 'text/plain';
      default:
        return 'application/octet-stream';
    }
  }

  /**
   * Upload a document to a routing slip
   * @param id - Routing slip ID
   * @param file - File to upload
   * @returns Observable<any>
   */
  uploadRoutingSlipDocument(id: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    
    return this.http.post(`${this.apiUrl}/${id}/upload`, formData);
  }

  /**
   * Mark routing slip as viewed (for ViewOnly permission)
   * @param id - Routing slip ID
   * @returns Observable<any>
   */
  markAsViewed(id: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/${id}/viewonly`, {}, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  /**
   * Request DocuSign signing URL. Body: routingId, documentType (1 = Letter, 2 = RoutingSlip).
   * Response JSON should include url | signingUrl | recipientViewUrl (or PascalCase variants).
   */
  requestDocuSignSigningUrl(
    routingId: string,
    documentType: SigningDocumentKind
  ): Observable<string> {
    return this.http
      .post<Record<string, unknown>>(`${this.apiUrl}/docusign/signing-url`, {
        routingId,
        documentType,
      })
      .pipe(
        map((body) => {
          const url = this.pickStringFromRecord(body, [
            'url',
            'signingUrl',
            'recipientViewUrl',
            'Url',
            'SigningUrl',
            'RecipientViewUrl',
          ]);
          return url;
        })
      );
  }
}
